import { useEffect, useState } from "react";
import api, { formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Zap, ArrowLeft, Send, CheckCircle2, RotateCcw, LifeBuoy } from "lucide-react";

export const AdminSupport = ({ onActivity }) => {
  const [data, setData] = useState({ tickets: [], open_priority: 0, open_total: 0, open_priority_unanswered: 0, pro_sla_hours: 4 });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [filterPU, setFilterPU] = useState(false);

  const load = () => api.get("/admin/support/tickets").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const fmtFirst = (min) => {
    if (min == null) return null;
    if (min <= 0) return "<1m";
    if (min < 60) return `${min}m`;
    return `${(min / 60).toFixed(1)}h`;
  };

  const openTicket = async (id) => {
    try { const { data } = await api.get(`/admin/support/tickets/${id}`); setActive(data); load(); onActivity && onActivity(); }
    catch { toast.error("Could not open ticket"); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${active.id}/reply`, { body: reply }); setReply(""); openTicket(active.id); toast.success("Reply sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { await api.post(`/admin/support/tickets/${active.id}/status`, { status }); openTicket(active.id); load(); }
    catch { toast.error("Could not update status"); }
  };

  if (active) {
    return (
      <div className="max-w-2xl space-y-4">
        <button data-testid="admin-support-back" onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-sm text-mute hover:text-ink"><ArrowLeft className="h-4 w-4" /> Support queue</button>
        <div className="bg-white border border-line rounded-xl p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-heading font-medium text-ink flex items-center gap-2">
                {active.priority && <Zap className="h-4 w-4 text-forest" />}{active.subject}
              </h2>
              <p className="text-xs text-mute mt-0.5">{active.user_name} · {active.tier}{active.priority ? ` · Priority (target ${active.sla_target_hours}h)` : ""}{active.first_response_minutes != null ? ` · First reply ${fmtFirst(active.first_response_minutes)}` : active.unanswered ? " · Awaiting first reply" : ""}</p>
            </div>
            {active.status === "resolved"
              ? <button data-testid="admin-reopen-btn" onClick={() => setStatus("open")} className="text-xs font-medium border border-line rounded-lg px-3 py-1.5 inline-flex items-center gap-1 hover:bg-stone-50"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
              : <button data-testid="admin-resolve-btn" onClick={() => setStatus("resolved")} className="text-xs font-medium border border-emerald-200 text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5 inline-flex items-center gap-1 hover:bg-emerald-100"><CheckCircle2 className="h-3.5 w-3.5" /> Mark resolved</button>}
          </div>
          <div className="mt-4 space-y-3">
            {(active.messages || []).map((m) => (
              <div key={m.id} className={`flex ${m.from === "support" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.from === "support" ? "bg-forest text-white" : "bg-stone-100 text-ink"}`}>
                  <p className="text-[11px] font-semibold opacity-80 mb-0.5">{m.from === "support" ? "Support" : m.author_name}</p>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <p className="text-[10px] opacity-60 mt-1">{formatDateTime(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 items-end">
            <textarea data-testid="admin-support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Reply to this member…" className="flex-1 border border-line rounded-lg px-3 py-2 text-sm resize-none" />
            <button data-testid="admin-support-reply-send" disabled={busy || !reply.trim()} onClick={sendReply} className="bg-forest text-white px-4 py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center gap-1.5"><Send className="h-4 w-4" /> Send</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-xl border border-forest/20 bg-forest/[0.03] p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5 text-forest shrink-0" />
          <p className="text-sm text-ink"><span className="font-semibold">{data.open_priority_unanswered}</span> priority unanswered · <span className="font-semibold">{data.open_priority}</span> priority · <span className="font-semibold">{data.open_total}</span> open. Pro target: {data.pro_sla_hours}h first response.</p>
        </div>
        <button data-testid="admin-filter-pu" onClick={() => setFilterPU((v) => !v)} className={`text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors ${filterPU ? "bg-forest text-white border-forest" : "border-line text-ink hover:bg-stone-50"}`}>
          {filterPU ? "Showing: Priority + unanswered" : "Filter: Priority + unanswered"}
        </button>
      </div>
      {(() => {
        const list = filterPU ? data.tickets.filter((t) => t.priority && t.unanswered && t.status === "open") : data.tickets;
        return list.length === 0 ? (
          <div data-testid="admin-support-empty" className="bg-white border border-line rounded-xl p-8 text-center">
            <LifeBuoy className="h-8 w-8 text-mute mx-auto mb-2" />
            <p className="text-sm text-mute">{filterPU ? "No priority unanswered tickets. All caught up." : "No support requests yet."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((t) => (
              <button key={t.id} data-testid={`admin-support-ticket-${t.id}`} onClick={() => openTicket(t.id)} className={`w-full text-left bg-white border rounded-xl p-4 hover:border-forest/40 transition-colors ${t.priority && t.status === "open" ? "border-forest/40" : "border-line"}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-ink truncate flex items-center gap-2">
                    {t.priority && <span data-testid="admin-priority-badge" className="text-[10px] font-semibold text-forest bg-forest/10 border border-forest/20 rounded-full px-2 py-0.5 uppercase inline-flex items-center gap-1"><Zap className="h-3 w-3" />Pro</span>}
                    {t.subject}
                    {t.unread_for_admin && <span className="inline-block w-2 h-2 rounded-full bg-terracotta" />}
                  </p>
                  <span className={`shrink-0 text-xs font-semibold rounded-full px-2.5 py-0.5 border ${t.status === "resolved" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{t.status}</span>
                </div>
                <p className="text-sm text-mute truncate mt-1">{t.user_name} · {t.tier} — {t.last_from === "support" ? "You: " : ""}{t.last_message}</p>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-[11px] text-mute">{formatDateTime(t.updated_at)}</p>
                  {fmtFirst(t.first_response_minutes) != null && <span data-testid="admin-first-response" className="text-[11px] text-emerald-700">First reply: {fmtFirst(t.first_response_minutes)}</span>}
                  {t.unanswered && <span data-testid="admin-unanswered-tag" className="text-[11px] font-medium text-amber-600">Awaiting reply</span>}
                </div>
              </button>
            ))}
          </div>
        );
      })()}
    </div>
  );
};
